import { useMemo } from 'react';
import { tarabeezaMarkDataUri, type MarkOptions } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

interface TarabeezaMarkProps extends Pick<MarkOptions, 'size' | 'light' | 'jewel' | 'radius'> {
  className?: string;
  /**
   * The mark is decorative wherever the word "Tarabeeza" is already on screen —
   * a screen reader announcing the logo twice is noise, not access.
   */
  decorative?: boolean;
}

/**
 * The Tarabeeza mark.
 *
 * Drawn by `tarabeezaMark` in `src/lib/ui-tokens/brand.ts` and never re-drawn here:
 * the SVG is authored once and shared by seven clients, and a second copy is a
 * second thing to get wrong. The tabletop ring removes itself below 48px inside
 * that function, so callers pass a size and stop thinking about it.
 *
 * The dark tile is used on BOTH themes. The tile is part of the lockup — the
 * mark is a table standing on a surface — and #1C1710 reads as an object on the
 * cream light ground as readily as on the black one. `light` is there for the
 * inverse case (the mark stamped on a white receipt) and is exposed rather than
 * being decided here.
 */
export function TarabeezaMark({
  size = 96,
  light,
  jewel,
  radius,
  className,
  decorative,
}: TarabeezaMarkProps) {
  const src = useMemo(
    () => tarabeezaMarkDataUri({ size, light, jewel, radius }),
    [size, light, jewel, radius],
  );

  return (
    <img
      src={src}
      width={size}
      height={size}
      alt={decorative ? '' : 'Tarabeeza'}
      aria-hidden={decorative || undefined}
      /* The artwork is square and the tile carries its own corner radius, so it
         must not be stretched by a flex parent. */
      className={cn('shrink-0 select-none', className)}
      draggable={false}
    />
  );
}
